import type { ChatElement, ToolCall } from '@gitlab-org/tui';
import { SecretRedactor } from '@gitlab-org/secret-redaction';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { ErrorHandler } from '@gitlab-org/errors';
import { Service, ServiceLifetime } from '@gitlab/needle';
import { ExitHandler } from '../../utils/exit';
import { UsageError } from '../../utils/usage_error';
import { CliInitializationService } from '../../cli_initialization_service';
import {
  AgentEventType,
  UserActionType,
  type UserAction,
  type ToolApprovalAction,
} from '../../backend/backend';
import { ParsedCliInput } from '../../parse';
import { SessionManager, type Session } from '../../sessions';
import { TerminalProgressService } from '../../utils/terminal_progress_service';
import { sliceAtCodePoint } from '../../utils/slice_at_code_point';
import { NotificationService } from '../../utils/notification_service';
import { SlashCommandService } from '../../slash_commands';
import { RunResultWriter } from './run_result_writer';

/**
 * Caps for a returned delegation value in the progress log; whichever hits first.
 * The full report stays on the delegation entry of the json result document.
 */
const DELEGATION_OUTPUT_MAX_LINES = 15;
const DELEGATION_OUTPUT_MAX_LENGTH = 1000;

@Service({
  dependencies: [
    Logger,
    ErrorHandler,
    SecretRedactor,
    CliInitializationService,
    ParsedCliInput,
    ExitHandler,
    SessionManager,
    TerminalProgressService,
    SlashCommandService,
    NotificationService,
    RunResultWriter,
  ],
  lifetime: ServiceLifetime.Singleton,
})
export class RunController {
  #logger: Logger;

  #errorHandler: ErrorHandler;

  #secretRedactor: SecretRedactor;

  #initService: CliInitializationService;

  #cliInput: ParsedCliInput;

  #exitHandler: ExitHandler;

  #sessionManager: SessionManager;

  #terminalProgress: TerminalProgressService;

  #slashCommandService: SlashCommandService;

  #notificationService: NotificationService;

  #resultWriter: RunResultWriter;

  /**
   * The sub-agent's most recent failed step, reported alongside a failed
   * delegation. Delegations are sequential, so one slot suffices.
   */
  #lastSubagentFailure?: { toolName: string; error: string };

  /**
   * Delegation ids whose start/outcome has been logged. Every checkpoint replays the
   * whole log and a pending delegation is re-emitted each time, so without this guard
   * one delegation produces a "Delegating..." line per checkpoint.
   */
  #announcedDelegations = new Set<string>();

  #settledDelegations = new Set<string>();

  constructor(
    logger: Logger,
    errorHandler: ErrorHandler,
    secretRedactor: SecretRedactor,
    initService: CliInitializationService,
    cliInput: ParsedCliInput,
    exitHandler: ExitHandler,
    sessionManager: SessionManager,
    terminalProgress: TerminalProgressService,
    slashCommandService: SlashCommandService,
    notificationService: NotificationService,
    resultWriter: RunResultWriter,
  ) {
    this.#logger = withPrefix(logger, '[RunController]');
    this.#errorHandler = errorHandler;
    this.#secretRedactor = secretRedactor;
    this.#initService = initService;
    this.#cliInput = cliInput;
    this.#exitHandler = exitHandler;
    this.#sessionManager = sessionManager;
    this.#terminalProgress = terminalProgress;
    this.#slashCommandService = slashCommandService;
    this.#notificationService = notificationService;
    this.#resultWriter = resultWriter;
  }

  async initialize(): Promise<void> {
    try {
      const { criticalError, existingSessionId } = await this.#initService.initialize();

      if (criticalError) {
        this.#logger.error(criticalError);
        await this.#exitHandler.exit(1);
        return;
      }

      const { session, sessionDetails } = await this.#sessionManager.createSession(
        existingSessionId,
        { skipHistoryRehydration: true },
      );

      // Headless has no UI to display an access denial, so fail fast when the
      // backend reports the session could not be created. (The TUI surfaces the
      // same reason in its UI instead — the backend only reports it.)
      if (sessionDetails.sessionRejectionReason) {
        this.#logger.error(
          `Failed to initialize workflow: ${sessionDetails.sessionRejectionReason}`,
        );
        await this.#exitHandler.exit(1);
        return;
      }

      try {
        await session.preinitialize();
      } catch (error) {
        this.#logger.warn('Session pre-initialization failed (non-critical)', error);
      }

      const { warnings, conflicts } = await this.#slashCommandService.registerDynamicProviders();
      warnings.forEach((warning) => this.#logger.warn(warning));
      conflicts.forEach((conflict) => {
        const losers = conflict.losers
          .map((loser) => `"${loser.displayPath}" (${loser.source})`)
          .join(', ');
        this.#logger.warn(
          `Skill conflict "${conflict.skillName}": using "${conflict.winner.displayPath}" (${conflict.winner.source}); skipped ${losers}`,
        );
      });
    } catch (error) {
      this.#errorHandler.handleError('GitLab Duo CLI initialization failed', error);
      await this.#exitHandler.exit(1);
    }
  }

  async execute(): Promise<void> {
    const session = this.#sessionManager.getActiveSession();
    if (!session || this.#cliInput.command?.name !== 'run') {
      throw new Error('Controller not initialized. Call initialize() first.');
    }

    const { goal, approval, rejectionReason, outputFormat } = this.#cliInput.command;
    const jsonMode = outputFormat === 'json';

    // Inside the try so a command that cannot run headlessly still yields
    // exactly one json-mode error document via the catch below.
    try {
      const action: UserAction = approval
        ? this.#buildApprovalAction(approval, rejectionReason)
        : await this.#buildPromptAction(goal);

      this.#logger?.info(`Executing workflow: ${JSON.stringify(action, null, 4)}`);

      const stream = session.sendMessageStream(action);
      const progressTrackedStream = this.#terminalProgress.trackStream(stream);

      let finalElement: ChatElement | undefined;
      for await (const item of progressTrackedStream) {
        // eslint-disable-next-line no-continue
        if (item.type === AgentEventType.Retry) continue; // ignore retry progress as there is no UI to update in headless mode

        this.#handleAgentEvent(item, session);
        if (session.isRootElement(item.id)) {
          finalElement = item;
        }
      }

      // Cancellation emits no closing delegation entry, so a delegation left open at
      // the end of the run must be reported here rather than silently dropped.
      this.#warnUnresolvedDelegations(session);

      // A non-interactive run is inherently a background/multitask case, so treat the
      // terminal as unfocused when deciding whether to notify.
      const failed = finalElement?.type === 'error';
      this.#notificationService.notify(failed ? 'error' : 'response_ready', { focused: false });

      const exitCode = failed ? 1 : 0;

      // stdout carries only the result; logs go to stderr. In json mode that
      // result is one machine-readable document, in text mode the response text.
      if (jsonMode) {
        this.#resultWriter.writeJson(
          session,
          failed
            ? { status: 'error', exitCode, error: this.#errorFrom(finalElement) }
            : { status: 'success', exitCode },
        );
      } else {
        this.#resultWriter.writeText(session);
      }

      await this.#exitHandler.exit(exitCode);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (error instanceof UsageError) {
        // Bad invocation, not a malfunction — the top-level handler prints
        // the message; an error-level "Workflow failed" log would be noise.
        this.#logger?.info(`Rejected non-interactive invocation: ${message}`);
      } else {
        this.#logger?.error('Workflow failed:', error instanceof Error ? error : undefined);
      }

      // A thrown stream skips the end-of-run check above, so a delegation that
      // was open when the failure hit is reported here as well.
      this.#warnUnresolvedDelegations(session);

      // A thrown failure still owes json-mode consumers exactly one document, so
      // emit an error result before the exception propagates. In text mode the
      // failure is already surfaced on stderr via the log above.
      if (jsonMode) {
        this.#resultWriter.writeJson(session, { status: 'error', exitCode: 1, error: message });
      }

      throw error;
    }
  }

  /** Extracts the error string from a terminal error element, if present. */
  #errorFrom(finalElement: ChatElement | undefined): string {
    return finalElement?.type === 'error' ? finalElement.error : 'Unknown error';
  }

  async #buildPromptAction(goal: string): Promise<UserAction> {
    // Expand any slash command first, then redact: a command body can inject
    // text containing secrets, so redaction must run on the final prompt.
    const expanded = await this.#slashCommandService.toPrompt(goal);
    if (expanded === undefined && this.#slashCommandService.isCommand(goal)) {
      // A known command with no headless prompt form (e.g. a plugin `run:`
      // command, whose approval flow is interactive-only). Sending the raw
      // slash text to the model would be meaningless, so fail fast instead.
      const commandName = goal.trim().split(/\s+/)[0];
      // UsageError: surfaced as a plain message + exit 1, not as a crash.
      throw new UsageError(
        `${commandName} is not supported in non-interactive mode; run it from the interactive CLI instead`,
      );
    }
    const rewritten = expanded !== undefined;
    const prompt = this.#secretRedactor.redactSecrets(expanded ?? goal, 'user-input');

    if (rewritten) {
      this.#logger?.info(`Slash command rewritten to: "${prompt}"`);
    }

    return { type: UserActionType.SendPrompt, prompt };
  }

  #buildApprovalAction(
    approval: 'approved' | 'rejected',
    rejectionReason?: string,
  ): ToolApprovalAction {
    if (approval === 'rejected') {
      return {
        type: UserActionType.SendToolApproval,
        toolId: '',
        toolName: '',
        approved: false,
        rejectionReason,
      };
    }
    return {
      type: UserActionType.SendToolApproval,
      toolId: '',
      toolName: '',
      approved: true,
      scope: 'once',
    };
  }

  #handleAgentEvent(element: ChatElement, session: Session): void {
    // Sub-agent entries are not rendered in headless output; only their
    // failures are remembered, for reporting on a failed delegation.
    if (!session.isRootElement(element.id)) {
      if (element.type === 'tool' && element.state.type === 'error') {
        this.#lastSubagentFailure = { toolName: element.name, error: element.state.error };
      }
      return;
    }

    switch (element.type) {
      // The completed message is the run's result; the result writer emits it.
      case 'message':
        break;

      case 'tool': {
        const toolCall = element as ToolCall;
        // The tool name is the stable discriminant: the formatted input can be
        // { tool: 'pending' } while the delegation's args are still streaming.
        if (toolCall.name === 'delegate_task') {
          this.#logDelegation(toolCall, this.#subagentNameOf(toolCall));
          break;
        }
        switch (toolCall.state.type) {
          case 'loading':
            this.#logger?.info(`Tool started: ${toolCall.name}`);
            break;
          case 'success':
            this.#logger?.info(`Tool completed: ${toolCall.id}`);
            break;
          case 'error':
            this.#logger?.info(`Tool completed: ${toolCall.id}`);
            this.#logger?.error(`Tool error: ${toolCall.state.error}`);
            break;
          case 'approval_request':
            // gitlab_backend event mapping emits one of these even though we don't get an actual approval request
            break;
          default:
            break;
        }
        break;
      }

      case 'error': {
        this.#logger?.error(`Error: ${element.error}`);
        break;
      }

      default:
        break;
    }
  }

  #logDelegation(toolCall: ToolCall, subagentName?: string): void {
    switch (toolCall.state.type) {
      case 'loading':
        // No name yet means the args are still streaming; the named version
        // of this element follows under the same id.
        if (subagentName === undefined) break;
        if (this.#announcedDelegations.has(toolCall.id)) break;
        this.#announcedDelegations.add(toolCall.id);
        this.#lastSubagentFailure = undefined;
        this.#logger?.info(`Delegating to ${subagentName}...`);
        break;
      case 'success': {
        if (!this.#markSettled(toolCall.id)) break;
        const output = this.#cappedOutput(this.#redact(toolCall.state.output));
        this.#logger?.info(
          output
            ? `Delegation to ${subagentName ?? 'sub-agent'} returned:\n${output}`
            : `Delegation to ${subagentName ?? 'sub-agent'} returned an empty answer`,
        );
        break;
      }
      case 'error':
        if (!this.#markSettled(toolCall.id)) break;
        this.#logger?.error(
          `Delegation to ${subagentName ?? 'sub-agent'} failed: ${this.#redact(toolCall.state.error)}`,
        );
        if (this.#lastSubagentFailure) {
          this.#logger?.error(
            `Last failed sub-agent step: ${this.#lastSubagentFailure.toolName} -> ${this.#redact(this.#lastSubagentFailure.error)}`,
          );
        }
        break;
      default:
        break;
    }

    if (toolCall.state.type === 'success' || toolCall.state.type === 'error') {
      this.#lastSubagentFailure = undefined;
    }
  }

  #redact(text: string): string {
    return this.#secretRedactor.redactSecrets(text, 'delegation-output');
  }

  #cappedOutput(output: string): string {
    const trimmed = output.trim();
    const lines = trimmed.split('\n');
    const capped =
      lines.length > DELEGATION_OUTPUT_MAX_LINES
        ? lines.slice(0, DELEGATION_OUTPUT_MAX_LINES).join('\n')
        : trimmed;
    const bounded =
      capped.length > DELEGATION_OUTPUT_MAX_LENGTH
        ? sliceAtCodePoint(capped, DELEGATION_OUTPUT_MAX_LENGTH)
        : capped;
    return bounded === trimmed ? trimmed : `${bounded}\n… (truncated)`;
  }

  #markSettled(toolId: string): boolean {
    if (this.#settledDelegations.has(toolId)) return false;
    this.#settledDelegations.add(toolId);
    return true;
  }

  #subagentNameOf(toolCall: ToolCall): string | undefined {
    return toolCall.input.tool === 'delegate_task' ? toolCall.input.subagentName : undefined;
  }

  #warnUnresolvedDelegations(session: Session): void {
    for (const element of session.viewElements) {
      if (
        element.type === 'tool' &&
        element.name === 'delegate_task' &&
        element.state.type === 'loading'
      ) {
        const subagentName = this.#subagentNameOf(element) ?? 'sub-agent';
        this.#logger?.warn(
          `Run ended with the delegation to ${subagentName} still in progress; no result was returned.`,
        );
      }
    }
  }
}
